import React, { useEffect, useMemo } from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useDebounce } from "use-debounce";

import CarouselComponent from "../../CarouselComponent";
import { IconPredefined } from "../../icons/Icon";
import { PredefinedIconType } from "../../icons/Icon/Predefined";
import Button from "../../NewForms/Button";
import NewHeadline from "../../NewHeadline";
import { jumpToElement, SideNavContainer } from "../SideNavContainer";
import useSideNavStore from "../SideNavContainer/store";

const Container = styled.div`
    padding-top: 50px;
    padding-bottom: 50px;
    ${({ theme }) => theme.paddings.containerOuter.left}
    ${({ theme }) => theme.paddings.containerOuter.right}
`;

const Contracts = styled.div`
    display: none;
    ${({ theme }) => theme.breakpoints.from.medium.css`
      display: flex;
    `}

    justify-content: center;
    gap: 20px;
    margin-top: 50px;
`;

const Contract = styled.div`
    flex: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 15px;
    margin-top: 27px;
    background-color: ${({ theme }) => theme.colors.grey.toString()};
    ${({ theme }) => theme.breakpoints.upTo.extraSmall.css`
      margin-right: 20px;
      margin-left: 20px;
    `}
    ${({ theme }) => theme.breakpoints.from.small.css`
      max-width: 350px;
      margin-right: auto;
      margin-left: auto;
    `}
    ${({ theme }) => theme.breakpoints.from.medium.css`
      max-width: 400px;
      margin-right: 0;
      margin-left: 0;
    `}
`;

const ContractHeader = styled.div`
    padding: 40px;
    background-color: ${({ theme }) => theme.colors.red.toString()};
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    min-height: 140px;
    ${({ theme }) => theme.breakpoints.from.medium.css`
      min-height: 240px;
    `}
    ${({ theme }) => theme.breakpoints.from.normal.css`
      min-height: 240px;
    `}
    ${({ theme }) => theme.breakpoints.from.large.css`
      min-height: 210px;
    `}
    ${({ theme }) => theme.breakpoints.from.extraLarge.css`
      min-height: 100px;
    `}
`;

const ContractBody = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 40px;
    padding: 40px;
    color: ${({ theme }) => theme.colors.blue.toString()};
    border-bottom-right-radius: 15px;
    border-bottom-left-radius: 15px;
`;

const ComponentHeadline = styled(NewHeadline)`
    font-size: 24px;
    line-height: normal;
    ${({ theme }) => theme.breakpoints.from.medium.css`
      font-size: 38px;
    `}
`;

const Headline = styled(ComponentHeadline)`
    font-weight: 900;
    color: white;
    text-align: center;
`;

const Recommendation = styled.div`
    position: absolute;
    left: 25%;
    width: 50%;
    padding-top: 12px;
    padding-bottom: 12px;
    margin: 0 auto;
    font-weight: 500;
    color: white;
    text-align: center;
    background-color: ${({ theme }) => theme.colors.blue.toString()};
    transform: translateY(-50%);
`;

const Subline = styled.div`
    margin-top: 10px;
    font-weight: 500;
    color: white;
    text-align: center;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 10px;
`;

const ListItem = styled.div`
    display: flex;
    gap: 20px;
`;

const carouselBreakpoints = {
    0: { items: 1 },
    1024: { items: 3 },
};

const Dot = styled.div`
    width: 10px;
    height: 10px;
    margin: 5px;
    background-color: ${({ active, theme }) =>
        active ? theme.colors.red.toString() : "transparent"};
    border: 1px solid ${({ theme }) => theme.colors.red.toString()};
    border-radius: 50%;

    &:hover {
        cursor: pointer;
    }
`;

const renderDotsItem = ({ isActive }) => {
    return <Dot active={isActive} />;
};

const LinkListGrid = ({ headline, contracts }) => {
    const [paddingLeft] = useSideNavStore((state) => [state.paddingLeft]);
    const theme = useTheme();

    const carouselStyles = css`
        ${theme.breakpoints.from.medium.css`display: none`}

        margin-left: -20px;
        margin-right: -20px;
    `;

    const contractCards = useMemo(
        () =>
            contracts.map((contract, index) => (
                <Contract key={index}>
                    {contract.isBestseller && (
                        <Recommendation>Empfehlung</Recommendation>
                    )}
                    <ContractHeader>
                        <Headline is={3}>{contract.itemHeadline}</Headline>
                        <Subline>{contract.itemSubline}</Subline>
                    </ContractHeader>
                    <ContractBody>
                        <List>
                            {contract.listItems.map((listItem, listIndex) => (
                                <ListItem key={listIndex}>
                                    <IconPredefined
                                        type={PredefinedIconType.check}
                                        color="red"
                                        size="25px"
                                        css={css`
                                            flex-shrink: 0;
                                        `}
                                    />
                                    <span>{listItem.text}</span>
                                </ListItem>
                            ))}
                        </List>
                        <Button
                            color="red"
                            {...(contract.link.href.startsWith("#")
                                ? {
                                      onClick: () =>
                                          jumpToElement(
                                              contract.link.href.slice(1)
                                          ),
                                  }
                                : {
                                      to: contract.link.href,
                                  })}
                            centered
                        >
                            {contract.link.text}
                        </Button>
                    </ContractBody>
                </Contract>
            )),
        [contracts]
    );
    return (
        <Container>
            <SideNavContainer paddingLeft={paddingLeft}>
                <ComponentHeadline is={2}>{headline}</ComponentHeadline>
                <div css={carouselStyles}>
                    <CarouselComponent
                        mouseTracking
                        items={contractCards}
                        responsive={carouselBreakpoints}
                        infinite
                        disableButtonsControls
                        renderDotsItem={renderDotsItem}
                        controlsStrategy="alternate"
                    />
                </div>
                <Contracts>{contractCards}</Contracts>
            </SideNavContainer>
        </Container>
    );
};

export default LinkListGrid;
